"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { Modal } from "@/components/ds/modal";
import { Button } from "@/components/ui-system";
import { useDashboardBuilder } from "@/contexts/DashboardBuilderContext";
import { createDashboardWidget } from "@/lib/api/endpoints/dashboards";
import { getDeviceById, listDevicesPage } from "@/lib/api/endpoints/devices";
import type { DashboardWidgetType } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

import { defaultWidgetSize, findNextWidgetSlot } from "./dashboard-layout-utils";
import type { AggregationKey, TimeRangeKey } from "./widget-config";

const TYPES: { value: DashboardWidgetType; label: string }[] = [
  { value: "line_chart", label: "Line chart" },
  { value: "gauge", label: "Gauge" },
  { value: "stat", label: "Stat" },
  { value: "table", label: "Table" },
];

const AGG: AggregationKey[] = ["avg", "min", "max"];
const TR: TimeRangeKey[] = ["last_1h", "last_24h", "last_7d"];

type AddWidgetModalProps = {
  tenantId: string;
  dashboardId: string;
};

export function AddWidgetModal({ tenantId, dashboardId }: AddWidgetModalProps) {
  const qc = useQueryClient();
  const { addModalOpen, setAddModalOpen, baseWidgets } = useDashboardBuilder();

  const [type, setType] = useState<DashboardWidgetType>("line_chart");
  const [deviceId, setDeviceId] = useState("");
  const [channel, setChannel] = useState("");
  const [aggregation, setAggregation] = useState<AggregationKey>("avg");
  const [timeRange, setTimeRange] = useState<TimeRangeKey>("last_24h");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const devicesQuery = useQuery({
    queryKey: ["builder-devices", tenantId],
    queryFn: () => listDevicesPage({ tenantId, limit: 200, offset: 0 }),
    enabled: addModalOpen && Boolean(tenantId),
    staleTime: 30_000,
  });

  const deviceQuery = useQuery({
    queryKey: ["device", deviceId],
    queryFn: () => getDeviceById(deviceId),
    enabled: addModalOpen && Boolean(deviceId),
  });

  const channels = useMemo(() => deviceQuery.data?.channels ?? [], [deviceQuery.data]);

  const reset = useCallback(() => {
    setType("line_chart");
    setDeviceId("");
    setChannel("");
    setAggregation("avg");
    setTimeRange("last_24h");
    setFormError(null);
  }, []);

  const onClose = useCallback(() => {
    setAddModalOpen(false);
    reset();
  }, [reset, setAddModalOpen]);

  const slot = useMemo(() => {
    const { width, height } = defaultWidgetSize(type);
    return findNextWidgetSlot(baseWidgets, width, height);
  }, [baseWidgets, type]);

  const onSubmit = useCallback(async () => {
    setFormError(null);
    if (!deviceId.trim()) {
      setFormError("Select a device.");
      return;
    }
    if (!channel.trim()) {
      setFormError("Select a channel.");
      return;
    }
    const keys = new Set(channels.map((c) => c.key));
    if (!keys.has(channel.trim())) {
      setFormError("Selected channel is not available on this device.");
      return;
    }
    const layout = slot;
    const config: JsonObject = {
      device_id: deviceId.trim(),
      channel: channel.trim(),
      time_range: timeRange,
      aggregation,
      refresh_interval_seconds: 30,
    };
    setSubmitting(true);
    try {
      await createDashboardWidget({
        dashboard_id: dashboardId,
        type,
        config,
        position_x: layout.position_x,
        position_y: layout.position_y,
        width: layout.width,
        height: layout.height,
      });
      await qc.invalidateQueries({ queryKey: ["dashboard-widgets", dashboardId] });
      onClose();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Could not create widget.");
    } finally {
      setSubmitting(false);
    }
  }, [
    deviceId,
    channel,
    channels,
    aggregation,
    timeRange,
    type,
    dashboardId,
    qc,
    onClose,
    slot,
  ]);

  const items = devicesQuery.data?.items ?? [];

  return (
    <Modal
      open={addModalOpen}
      onClose={onClose}
      title="Add widget"
      description="Creates a widget on this dashboard using the standard telemetry config shape."
      size="md"
      footer={
        <div className="flex flex-wrap justify-end gap-ds-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={() => void onSubmit()} disabled={submitting}>
            {submitting ? "Adding…" : "Add widget"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-ds-4 overflow-y-auto py-ds-1 text-caption">
        {formError ? (
          <p className="rounded-xl border border-error/30 bg-error/5 px-ds-3 py-ds-2 text-error" role="alert">
            {formError}
          </p>
        ) : null}
        <label className="flex flex-col gap-ds-1">
          <span className="font-medium text-muted">Widget type</span>
          <select
            className="ui-control py-ds-2"
            value={type}
            onChange={(e) => setType(e.target.value as DashboardWidgetType)}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-ds-1">
          <span className="font-medium text-muted">Device</span>
          <select
            className="ui-control py-ds-2"
            value={deviceId}
            onChange={(e) => {
              setDeviceId(e.target.value);
              setChannel("");
            }}
          >
            <option value="">Choose device…</option>
            {items.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name?.trim() || d.device_uid}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-ds-1">
          <span className="font-medium text-muted">Channel</span>
          <select
            className="ui-control py-ds-2"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            disabled={!deviceId || deviceQuery.isLoading}
          >
            <option value="">{deviceId ? "Choose channel…" : "Select a device first"}</option>
            {channels.map((c) => (
              <option key={c.id} value={c.key}>
                {c.label} ({c.key})
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-ds-1">
          <span className="font-medium text-muted">Aggregation</span>
          <select
            className="ui-control py-ds-2"
            value={aggregation}
            onChange={(e) => setAggregation(e.target.value as AggregationKey)}
          >
            {AGG.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-ds-1">
          <span className="font-medium text-muted">Time range</span>
          <select
            className="ui-control py-ds-2"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRangeKey)}
          >
            {TR.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </label>
        <p className="text-micro text-muted">
          Grid slot: row {slot.position_y + 1}, col {slot.position_x + 1} · {slot.width}×{slot.height} cells
        </p>
      </div>
    </Modal>
  );
}
